import * as React from "react";
import {
  // Design-system picker catalog (Files group)
  Folder,
  FolderOpen,
  FileText,
  Files,
  BookOpen,
  // Ideas
  Lightbulb,
  Target,
  Rocket,
  Zap,
  Brain,
  GraduationCap,
  // Categories
  Code,
  Globe,
  ShieldCheck,
  Lock,
  Settings,
  Database,
  // People
  MessageCircle,
  HelpCircle,
  Users,
  Briefcase,
  // Objects
  Package,
  Heart,
  Star,
  Palette,
  Layers,
  Landmark,
  // Extras that appear in practice but aren't in the DS picker (added so
  // the docs-site never falls back to raw text for known lucide values):
  FileCode,
  FileQuestion,
  FileJson,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  ClipboardList,
  BookMarked,
  Bookmark,
  Library,
  Tag,
  Wrench,
  Hammer,
  Server,
  Cloud,
  Terminal,
  Cpu,
  Bug,
  Sparkles,
  Flag,
  Calendar,
  Clock,
  Mail,
  Phone,
  Home,
  Building,
  Store,
  DollarSign,
  CreditCard,
  ShoppingCart,
  type LucideIcon,
} from "lucide-react";
import { FolderIcon as DSFolderIcon } from "@siena-ai/design-system";

// Curated kebab-case → component map covering the DS picker catalog plus
// common extras we've seen in the wild (e.g. `file-code`). Keep this
// deliberately small so the bundle stays lean — if a value lands here that
// isn't in the map we fall back to the generic `Folder` lucide icon, never
// raw text.
const LUCIDE_ICON_MAP: Record<string, LucideIcon> = {
  folder: Folder,
  "folder-open": FolderOpen,
  "file-text": FileText,
  files: Files,
  "book-open": BookOpen,
  lightbulb: Lightbulb,
  target: Target,
  rocket: Rocket,
  zap: Zap,
  brain: Brain,
  "graduation-cap": GraduationCap,
  code: Code,
  globe: Globe,
  shield: ShieldCheck,
  "shield-check": ShieldCheck,
  lock: Lock,
  settings: Settings,
  database: Database,
  "message-circle": MessageCircle,
  "help-circle": HelpCircle,
  users: Users,
  briefcase: Briefcase,
  package: Package,
  heart: Heart,
  star: Star,
  palette: Palette,
  layers: Layers,
  landmark: Landmark,
  // Extras
  "file-code": FileCode,
  "file-question": FileQuestion,
  "file-json": FileJson,
  "file-spreadsheet": FileSpreadsheet,
  "file-image": FileImage,
  "file-video": FileVideo,
  "file-audio": FileAudio,
  "file-archive": FileArchive,
  "clipboard-list": ClipboardList,
  "book-marked": BookMarked,
  bookmark: Bookmark,
  library: Library,
  tag: Tag,
  wrench: Wrench,
  hammer: Hammer,
  server: Server,
  cloud: Cloud,
  terminal: Terminal,
  cpu: Cpu,
  bug: Bug,
  sparkles: Sparkles,
  flag: Flag,
  calendar: Calendar,
  clock: Clock,
  mail: Mail,
  phone: Phone,
  home: Home,
  building: Building,
  store: Store,
  "dollar-sign": DollarSign,
  "credit-card": CreditCard,
  "shopping-cart": ShoppingCart,
};

export interface FolderIconSafeProps {
  emoji?: string | null;
  className?: string;
  iconClassName?: string;
}

// Defensive wrapper around the design-system `FolderIcon`. The DS version
// only knows ~25 lucide icons; anything outside that set either falls back to
// a generic folder, or — if the stored value has stray whitespace / casing
// issues that break its `.startsWith("lucide:")` probe — renders the raw
// string verbatim, which is what users were seeing.
//
// This wrapper:
//   1. trims the value and case-normalizes the `lucide:` prefix
//   2. looks up the icon against a broader local map that includes
//      `file-code` and other common values found in real data
//   3. if the lucide key is unknown, still renders a generic `Folder` icon
//      instead of ever printing the raw string
//   4. defers to the DS component for plain emoji values so we keep its
//      typography rules for non-lucide folders
export const FolderIconSafe: React.FC<FolderIconSafeProps> = ({
  emoji,
  className,
  iconClassName,
}) => {
  const raw = (emoji ?? "").trim();

  if (raw && raw.toLowerCase().startsWith("lucide:")) {
    const key = raw.slice("lucide:".length).trim().toLowerCase();
    const Icon = LUCIDE_ICON_MAP[key] ?? Folder;
    const cn = [
      "h-4 w-4 text-muted-foreground shrink-0",
      iconClassName ?? "",
    ]
      .filter(Boolean)
      .join(" ");
    return <Icon className={cn} />;
  }

  return (
    <DSFolderIcon
      emoji={raw || undefined}
      className={className}
      iconClassName={iconClassName}
    />
  );
};
